import { useEffect, useState } from "react";

/**
 * Anima un numero de `start` a `target` al montar.
 * - Parte siempre de `start` (sin flash del valor final).
 * - Un solo ciclo limpio: cancela rAF al desmontar / al reiniciar.
 * - Respeta prefers-reduced-motion.
 *
 * @param {number} target
 * @param {{duration?:number, start?:number}} opts
 * @returns {number} valor actual (sin redondear)
 */
export function useCountUp(target, { duration = 1200, start = 0 } = {}) {
    const [value, setValue] = useState(() =>
        typeof target === "number" && !isNaN(target) ? start : 0
    );

    useEffect(() => {
        if (typeof target !== "number" || isNaN(target)) return undefined;

        const reduce =
            typeof window !== "undefined" &&
            window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        if (reduce) {
            setValue(target);
            return undefined;
        }

        let raf = 0;
        let alive = true;
        const from = start;
        const to = target;
        const t0 = performance.now();
        setValue(from);

        const tick = (now) => {
            if (!alive) return;
            const p = Math.min(1, (now - t0) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setValue(from + (to - from) * eased);
            if (p < 1) raf = requestAnimationFrame(tick);
            else setValue(to);
        };

        raf = requestAnimationFrame(tick);

        return () => {
            alive = false;
            cancelAnimationFrame(raf);
        };
    }, [target, duration, start]);

    return value;
}

export default useCountUp;
